/*
/joho1/dncl の欄。対応表、表記のコード、Python をその場で動かす欄、書き換えの判定の欄。
Python はブラウザの Pyodide で動く (lib/code-execution の executeBrowserCode)。実行を押すまで Pyodide は取りに行かない
*/
import { useState, type ReactNode } from "react";
import { RunnableCodeBlock, type CodeExecutionResult } from "@noiman/ui/runnable-code-block";
import { PLAIN, normalizeOutput, type Drill, type Example, type Row } from "./content";

const LINE = 22;
/** 編集欄の高さ。書き足す 1 行と横送りの帯の分を足す (390 の幅で 6 行しか見えず、縦と横の送りが両方出た) */
function editorHeight(...codes: string[]) {
  const lines = Math.max(...codes.map((c) => c.replace(/\n$/, "").split("\n").length));
  return `${(lines + 1) * LINE + 32}px`;
}

async function runPython(code: string): Promise<CodeExecutionResult> {
  const { executeBrowserCode } = await import("@/lib/code-execution");
  const result = await executeBrowserCode({ language: "python", code });
  return { output: result.output, error: result.error };
}

/** 表記のコード。収まらない行は空白と和字の所で折り返す (390 の幅でも 1440 の半分の列でも右が切れて見えた) */
export function NotationCode({ code, label }: { code: string; label: string }) {
  return (
    <figure className="m-0 min-w-0">
      <figcaption className="mb-1 text-[15px] font-bold text-sub">{label}</figcaption>
      <pre className="m-0 overflow-x-auto whitespace-pre-wrap rounded-md border border-main bg-second p-4 font-mono text-[15px] leading-[1.7] text-main">
        <code>{code}</code>
      </pre>
    </figure>
  );
}

export function PythonRunner({ code, label = "Python (実行できます)" }: { code: string; label?: string }) {
  return (
    <div className="min-w-0">
      <p className="mb-1 text-[15px] font-bold text-sub">{label}</p>
      <div className="[&>div]:my-0">
        <RunnableCodeBlock initialCode={code} language="python" height={editorHeight(code)} onExecute={runPython} fontSize={15} />
      </div>
    </div>
  );
}

export function ExampleBlock({ example }: { example: Example }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2" data-dncl-example={example.id}>
      <NotationCode code={example.joho} label="情報 I の表記" />
      <PythonRunner code={example.python} />
      <div className="lg:col-span-2">
        <NotationCode code={example.expected} label="期待の出力 (python3 で走らせた結果)" />
      </div>
    </div>
  );
}

function Cell({ text }: { text: string }) {
  if (text.startsWith(PLAIN)) {
    return <span className="text-[16px] leading-[1.7] text-sub [overflow-wrap:anywhere]">{text.slice(PLAIN.length)}</span>;
  }
  return (
    <code className="block whitespace-pre-wrap font-mono text-[15px] leading-[1.7] text-main [word-break:keep-all] [overflow-wrap:anywhere]">
      {text}
    </code>
  );
}

const COLS = [
  { key: "old", label: "旧 DNCL" },
  { key: "joho", label: "情報 I の表記" },
  { key: "python", label: "Python" },
] as const;

/**
 * 3 つの表記の対応表。768px 以上は表、それより狭い幅は 1 行ずつを縦に積む (同じ table を CSS で組み替える。横にはみ出さない)
 */
export function CorrespondenceTable({ rows }: { rows: readonly Row[] }) {
  return (
    <table className="w-full border-collapse text-left max-md:block" data-dncl-table>
      <caption className="sr-only">旧 DNCL、情報 I の共通テスト用プログラム表記、Python の対応表</caption>
      <thead className="max-md:hidden">
        <tr className="border-b-2 border-main">
          <th scope="col" className="w-[22%] px-3 py-2 text-[16px] font-bold text-sub">
            項目
          </th>
          {COLS.map((c) => (
            <th key={c.key} scope="col" className="w-[26%] px-3 py-2 text-[16px] font-bold">
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="max-md:block max-md:space-y-3">
        {rows.map((row) => (
          <tr
            key={row.topic}
            className="border-b border-main align-top max-md:block max-md:rounded-md max-md:border max-md:bg-surface max-md:p-3"
          >
            <th scope="row" className="px-3 py-3 text-[16px] font-bold max-md:block max-md:p-0 max-md:pb-2 max-md:text-[17px]">
              {row.topic}
              {row.note ? (
                <span className="mt-1 block text-[15px] font-normal leading-[1.7] text-sub [overflow-wrap:anywhere]">
                  {row.note}
                </span>
              ) : null}
            </th>
            {COLS.map((c) => (
              <td key={c.key} className="px-3 py-3 max-md:block max-md:border-t max-md:border-main max-md:px-0 max-md:py-2">
                <span className="mb-1 block text-[15px] font-bold text-sub md:hidden">{c.label}</span>
                <Cell text={row[c.key]} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

type Verdict = { ok: boolean; got: string } | null;

/** 書き換えの 1 題。実行すると出力を期待の出力と比べ、合っているかを欄の下に出す */
export function DrillCard({ drill }: { drill: Drill }) {
  const [verdict, setVerdict] = useState<Verdict>(null);
  const onExecute = async (code: string): Promise<CodeExecutionResult> => {
    const result = await runPython(code);
    const got = normalizeOutput(result.output);
    setVerdict({ ok: !result.error && got === normalizeOutput(drill.expected), got });
    return result;
  };
  return (
    <section className="rounded-md border border-main bg-surface p-4 sm:p-5" data-dncl-drill={drill.id}>
      <h3 className="m-0 text-[19px] font-bold">{drill.title}</h3>
      <p className="mt-2 text-[17px] leading-[1.8] text-sub">{drill.hint}</p>
      <div className="mt-3 grid gap-4 lg:grid-cols-2">
        <NotationCode code={drill.joho} label="情報 I の表記" />
        <NotationCode code={drill.expected} label="期待の出力" />
      </div>
      <div className="mt-4">
        <p className="mb-1 text-[15px] font-bold text-sub">Python に書き換えて実行する</p>
        <div className="[&>div]:my-0">
          <RunnableCodeBlock
            initialCode={drill.starter}
            language="python"
            height={editorHeight(drill.starter, drill.answer)}
            onExecute={onExecute}
            fontSize={15}
          />
        </div>
      </div>
      <div aria-live="polite" className="mt-3 min-h-[1.8em] text-[17px] font-bold">
        {verdict === null ? null : verdict.ok ? (
          <p className="m-0 text-primary">期待の出力と一致しました。</p>
        ) : (
          <p className="m-0 text-main">まだ一致していません。出力を期待の出力と 1 行ずつ見比べてください。</p>
        )}
      </div>
      <details className="mt-2">
        <summary className="cursor-pointer text-[17px] font-bold text-primary">答えの例を見る</summary>
        <pre className="mt-2 overflow-x-auto rounded-md border border-main bg-second p-4 font-mono text-[15px] leading-[1.7]">
          <code>{drill.answer}</code>
        </pre>
      </details>
    </section>
  );
}

export function SectionTitle({ id, children }: { id: string; children: ReactNode }) {
  return (
    <h2 id={id} className="mb-4 mt-12 scroll-mt-24 border-b-2 border-main pb-2 text-[24px] font-bold leading-[1.5] first:mt-0 md:text-[26px]">
      {children}
    </h2>
  );
}
